import { useMemo } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import BrandsService from "../../../../service/BrandsService";
import SubcategoriesService from "../../../../service/SubcategoriesService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import useCatalogForm from "../../hooks/useCatalogForm";
import { buildSearchText, getToken } from "../../utils/adminUtils";
import {
  findNewestIdBy,
  linkToSubcategories,
  localizedDescription,
  subcategoryOptions,
} from "../../utils/catalogUtils";
import CatalogTable from "../../components/list/CatalogTable";
import FormDialog from "../../components/form/FormDialog";
import FormField from "../../components/form/FormField";
import SubcategoryPicker from "../../components/form/SubcategoryPicker";

const LIST_PATH = "/dashboard-listbrands";
const MAX_CHIPS = 3;

// Marcas + subcategorias (cada subcategoria traz as marcas vinculadas em `brands`)
const loadBrandsWithSubcategories = async () => {
  const [brandsRes, subRes] = await Promise.all([
    new BrandsService().getBrands(getToken()),
    new SubcategoriesService().getSubcategories(getToken()),
  ]);
  return [
    {
      brands: expectCode(brandsRes, 200001).result || [],
      subcategories: expectCode(subRes, 200001).result || [],
    },
  ];
};

const AdminBrands = ({ startCreating = false }) => {
  const { showErrorToast } = useToastContext();
  const { items, loading, loadError, reload } = useAdminList(loadBrandsWithSubcategories);
  const { run, runningId } = useAdminAction();
  const data = items[0];

  const options = useMemo(() => subcategoryOptions(data?.subcategories), [data]);

  const linkedByBrand = useMemo(() => {
    const map = new Map();
    (data?.subcategories || []).forEach((sub) =>
      (sub.brands || []).forEach((brand) => {
        if (!map.has(brand.id)) map.set(brand.id, []);
        map.get(brand.id).push({ id: sub.id, label: localizedDescription(sub) });
      }),
    );
    return map;
  }, [data]);

  const buildInitial = (item) => ({
    description: item?.description || "",
    subcategoryIds: [],
    errors: {},
  });

  const { form, openCreate, openEdit, close, update } = useCatalogForm({
    startCreating,
    listPath: LIST_PATH,
    buildInitial,
  });

  const rows = useMemo(
    () =>
      (data?.brands || []).map((brand) => {
        const linked = linkedByBrand.get(brand.id) || [];
        return {
          ...brand,
          linked,
          linkedCount: linked.length,
          searchText: buildSearchText([brand.description, ...linked.map((sub) => sub.label)]),
        };
      }),
    [data, linkedByBrand],
  );

  const save = async () => {
    const errors = {};
    if (!form.description.trim()) errors.description = localeText("adminFormRequired");
    // Como no admin clássico, uma marca nova precisa de ao menos uma subcategoria
    if (form.mode === "create" && form.subcategoryIds.length === 0) {
      errors.subcategories = localeText("chooseSubcategory");
    }
    if (Object.keys(errors).length) {
      update({ errors });
      return;
    }

    const description = form.description.trim();
    const { item, subcategoryIds, mode } = form;
    let failedLinks = 0;

    const ok = await run(
      "form",
      async () => {
        const service = new BrandsService();
        const res =
          mode === "create"
            ? await service.postRegisterBrands(getToken(), description)
            : await service.postEditBrands(getToken(), description, item.id);
        expectCode(res, 200000);

        if (subcategoryIds.length === 0) return;
        let brandId = item?.id;
        if (mode === "create") {
          const brandsRes = await service.getBrands(getToken());
          brandId = findNewestIdBy(brandsRes?.result, (brand) => brand.description === description);
        }
        failedLinks = brandId == null
          ? subcategoryIds.length
          : await linkToSubcategories("brand", brandId, subcategoryIds);
      },
      "adminCatalogSaved",
    );

    if (failedLinks > 0) showErrorToast({ detail: "adminCatalogLinkFailed" });
    if (ok) {
      close();
      reload();
    }
  };

  return (
    <div className="admin-list">
      <FormDialog
        visible={!!form}
        title={localeText(form?.mode === "edit" ? "adminCatalogEditBrand" : "addNewBrand")}
        subtitle={form?.mode === "edit" ? form.item?.description : localeText("brandFields")}
        saving={runningId === "form"}
        onSave={save}
        onCancel={close}
      >
        {form && (
          <>
            <FormField
              label={localeText("descriptionBrand")}
              htmlFor="brand-description"
              error={form.errors.description}
              required
            >
              <input
                id="brand-description"
                className="admin-input"
                value={form.description}
                onChange={(e) =>
                  update({
                    description: e.target.value,
                    errors: { ...form.errors, description: null },
                  })
                }
                autoFocus
                autoComplete="off"
              />
            </FormField>
            <SubcategoryPicker
              options={options}
              lockedOptions={form.mode === "edit" ? linkedByBrand.get(form.item.id) || [] : []}
              value={form.subcategoryIds}
              onChange={(subcategoryIds) =>
                update({ subcategoryIds, errors: { ...form.errors, subcategories: null } })
              }
              required={form.mode === "create"}
              error={form.errors.subcategories}
              hint={form.mode === "edit" ? localeText("adminCatalogLinkHint") : null}
            />
          </>
        )}
      </FormDialog>

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("searchByBrand")}
        createLabel={localeText("newBrand")}
        onCreate={openCreate}
        sortField="description"
        minWidth="44rem"
      >
        <Column
          header={localeText("descriptionBrand")}
          field="description"
          body={(brand) => <span className="admin-strong">{brand.description || "—"}</span>}
          sortable
        />
        <Column
          header={localeText("adminCatalogSubcategories")}
          field="linkedCount"
          body={(brand) =>
            brand.linkedCount ? (
              <div className="admin-chips">
                {brand.linked.slice(0, MAX_CHIPS).map((sub) => (
                  <span key={sub.id} className="admin-chip">
                    {sub.label}
                  </span>
                ))}
                {brand.linkedCount > MAX_CHIPS && (
                  <span className="admin-chip">+{brand.linkedCount - MAX_CHIPS}</span>
                )}
              </div>
            ) : (
              <span className="admin-muted">—</span>
            )
          }
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(brand) => (
            <button type="button" className="admin-btn" onClick={() => openEdit(brand)}>
              <i className="pi pi-pencil" aria-hidden="true" />
              {localeText("edit")}
            </button>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminBrands;
